import { AnimatePresence, useAnimationControls, type Variants } from 'framer-motion';
import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import type { ConnectWithSocialParam, PasskeyParams } from '../../actions/connect';
import RetryIconCircle from '../../assets/icons/RetryIconCircle';
import CircleSpinner from '../../components/circleSpinner';
import { PageContent } from '../../components/modal/styles';
import SocialIcon from '../../components/socialLogin/Icons';
import SquircleSpinner from '../../components/squircleSpinner';
import { isCoinbase, isPasskeySmartWallet, isWalletConnect } from '../../config/wallet';
import { useContext } from '../../context';
import { useAccount } from '../../hooks/useAccount';
import useConnect from '../../hooks/useConnect';
import useIsMobile from '../../hooks/useIsMobile';
import { useLastConnectorId } from '../../hooks/useLastConnectorId';
import useLocales from '../../hooks/useLocales';
import { useWalletConnectModal } from '../../hooks/useWalletConnectModal';
import { useWalletConnectUri } from '../../hooks/useWalletConnectUri';
import type { WalletProps } from '../../hooks/useWalletsProps';
import { formatString } from '../../locales';
import { openMobileUrl } from '../../utils';
import { ConnectingAnimation, ConnectingContainer, RetryButton, RetryIconContainer, StatusContainer } from './styles';

export const States = {
  CONNECTED: 'connected',
  CONNECTING: 'connecting',
  FAILED: 'failed',
  REJECTED: 'rejected',
  NOTCONNECTED: 'notconnected',
  UNAVAILABLE: 'unavailable',
};

const contentVariants: Variants = {
  initial: {
    opacity: 0,
    scale: 0.95,
  },
  animate: {
    opacity: 1,
    scale: 1,
    transition: {
      ease: [0.16, 1, 0.3, 1],
      duration: 0.25,
    },
  },
};

type WalletInfo = {
  id: string;
  name: string;
  shortName: string;
  icon: ReactNode;
  iconShape?: 'squircle' | 'circle' | 'square';
  iconShouldShrink?: boolean;
  isAuth: boolean;
  passkey?: { isRegistering: boolean };
};

const Page = ({
  wallet,
  authParams,
  passkeyParams,
}: {
  wallet: WalletProps;
  authParams?: ConnectWithSocialParam;
  passkeyParams?: PasskeyParams;
}) => {
  const locales = useLocales();
  const [status, setStatus] = useState(States.CONNECTING);
  const controller = useAnimationControls();
  const { config } = useContext();
  const {
    uri,
    requestUri,
    available: wcAvailable,
    error: requestWCUriError,
  } = useWalletConnectUri(wallet?.connector?.id);
  const isMobile = useIsMobile();
  const { updateLastConnectorId } = useLastConnectorId();
  const { openW3m, error: openW3mError } = useWalletConnectModal();
  const { isConnected } = useAccount();

  const handleError = useCallback((error) => {
    if (error) {
      console.log('connect error', error.code, error.message, error.name, error);
      if (error.code) {
        // https://github.com/MetaMask/eth-rpc-errors/blob/main/src/error-constants.ts
        switch (error.code) {
          case -32002:
            setStatus(States.NOTCONNECTED);
            break;
          case 4001:
            setStatus(States.REJECTED);
            break;
          default:
            setStatus(States.FAILED);
            break;
        }
      } else {
        // Sometimes the error doesn't respond with a code
        if (error.message) {
          switch (error.message) {
            case 'User rejected request':
              setStatus(States.REJECTED);
              break;
            default:
              setStatus(States.FAILED);
              break;
          }
        }
      }
    }
  }, []);

  const connectCallback = useMemo(
    () => ({
      onMutate: ({ connector }) => {
        if (connector) {
          setStatus(States.CONNECTING);
        } else {
          setStatus(States.UNAVAILABLE);
        }
      },
      onSettled: (data, error: any) => {
        handleError(error);
      },
    }),
    [handleError]
  );

  const { connect } = useConnect(connectCallback);

  const isPasskey = useMemo(() => {
    return wallet.connector.id === 'passkeySmartWallet';
  }, [wallet]);

  const walletInfo: WalletInfo = useMemo(() => {
    let icon = wallet.icon;
    if (authParams?.socialType) {
      icon = <SocialIcon authType={authParams.socialType} />;
    }

    const passkey = isPasskey ? { isRegistering: passkeyParams?.isRegistering ?? false } : undefined;

    return {
      id: wallet.id,
      name: wallet.name!,
      shortName: wallet.shortName ?? wallet.name!,
      icon,
      iconShape: wallet.iconShape ?? 'circle',
      iconShouldShrink: wallet.iconShouldShrink,
      isAuth: Boolean(authParams?.socialType),
      passkey,
    };
  }, [wallet, authParams, passkeyParams, isPasskey]);

  const suggestedExtension = useMemo(() => {
    return wallet?.downloadUrls
      ? {
          name: Object.keys(wallet?.downloadUrls)[0],
          label:
            Object.keys(wallet.downloadUrls)[0]!.charAt(0).toUpperCase() +
            Object.keys(wallet.downloadUrls)[0]!.slice(1),
          url: wallet?.downloadUrls[Object.keys(wallet.downloadUrls)[0]!],
        }
      : undefined;
  }, [wallet]);

  const toggleAnim = async () => {
    controller.stop();
    controller.set('initial');
    // await controller.start('exit');
    await controller.start('animate');
  };

  const onConnect = () => {
    console.log('connect start, id:', wallet?.connector?.id);

    setStatus(States.CONNECTING);
    if (
      isMobile &&
      wallet.connector.chainType === 'evm' &&
      wallet.getWalletConnectDeeplink &&
      !isCoinbase(wallet.connector.id) &&
      !wallet.isInstalled
    ) {
      if (isWalletConnect(wallet.connector.id)) {
        console.log('open walletConnect modal...');
        openW3m();
        return;
      } else if (wcAvailable) {
        console.log('request walletConnect uri...');
        requestUri();
        return;
      }
    }

    if (wallet?.isInstalled && wallet?.connector) {
      if (authParams?.socialType) {
        config.storage?.setItem('recentConnectorId', wallet.connector.id);
        updateLastConnectorId(wallet.connector.id);
      }
      connect({ connector: wallet?.connector, authParams, passkeyParams });
    } else {
      setStatus(States.UNAVAILABLE);
    }
  };

  useEffect(() => {
    const connectTimeout = setTimeout(onConnect, 200);
    return () => {
      clearTimeout(connectTimeout);
    };
  }, []);

  useEffect(() => {
    if (uri && isMobile && wallet.connector.chainType === 'evm' && wallet.getWalletConnectDeeplink) {
      const url = wallet.getWalletConnectDeeplink(uri);
      console.log('openMobileUrl', url);
      openMobileUrl(url);
    }
  }, [uri]);

  useEffect(() => {
    if (isMobile && requestWCUriError) {
      handleError(requestWCUriError);
    }
  }, [requestWCUriError, handleError]);

  useEffect(() => {
    if (isMobile && isWalletConnect(wallet.connector.id) && openW3mError) {
      handleError(openW3mError);
    }
  }, [openW3mError, handleError]);

  useEffect(() => {
    return () => {
      // connect status  -> connected / disconnected
      if (!isConnected) {
        config.setState((x) => ({
          ...x,
          // Keep existing connector connected in case of error
          status: x.current ? 'connected' : 'disconnected',
        }));
      }
    };
  }, [isConnected]);

  const connectingH = useMemo(() => {
    const walletName = walletInfo.shortName || walletInfo.name || 'Wallet';
    if (status == States.CONNECTED) {
      return locales.connectingConnected;
    } else if (status == States.FAILED) {
      return locales.connectingFailed;
    } else if (status == States.REJECTED) {
      return locales.connectingRejected;
    } else if (status == States.NOTCONNECTED) {
      return formatString(locales.connectingNotconnected, walletName);
    } else if (status == States.UNAVAILABLE) {
      if (suggestedExtension) {
        return locales.connectingUnavailable;
      } else {
        if (isPasskeySmartWallet(walletInfo.id)) {
          return locales.connectingPasskeyUnavailable;
        }
        return formatString(locales.connectingInstall, walletName);
      }
    }
    return locales.connectingRequest;
  }, [status, locales, walletInfo, suggestedExtension]);

  const connectingP = useMemo(() => {
    const walletName = walletInfo.shortName || walletInfo.name || 'Wallet';
    if (status == States.CONNECTED) {
      return locales.connectingConnectedDesc;
    } else if (status == States.FAILED) {
      return locales.connectingFailedDesc;
    } else if (status == States.REJECTED) {
      return locales.connectingRejectedDesc;
    } else if (status == States.NOTCONNECTED) {
      return formatString(locales.connectingNotconnectedDesc, walletName);
    } else if (status == States.UNAVAILABLE) {
      if (suggestedExtension) {
        return formatString(locales.connectingUnavailableDesc, walletName, suggestedExtension.label);
      } else {
        if (isPasskeySmartWallet(walletInfo.id)) {
          return locales.connectingPasskeyUnavailableDesc;
        }
        return formatString(locales.connectingInstallDesc, walletName);
      }
    }
    if (walletInfo.isAuth) {
      return locales.connectingRequestAuthDesc;
    } else if (walletInfo.passkey) {
      if (walletInfo.passkey.isRegistering) {
        return locales.setupPasskeyPrompt;
      } else {
        return locales.passkeyPrompt;
      }
    }
    return locales.connectingRequestDesc;
  }, [status, locales, walletInfo, suggestedExtension]);

  useEffect(() => {
    if (connectingH) {
      toggleAnim();
    }
  }, [connectingH]);

  return (
    <PageContent>
      <ConnectingContainer>
        <ConnectingAnimation
          $shake={status === States.FAILED || status === States.REJECTED}
          $circle={walletInfo.iconShape === 'circle'}
        >
          <AnimatePresence>
            {(status === States.FAILED || status === States.REJECTED) && (
              <RetryButton
                aria-label='Retry'
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.8 }}
                whileTap={{ scale: 0.9 }}
                transition={{ duration: 0.1 }}
                onClick={onConnect}
              >
                <RetryIconContainer>
                  <RetryIconCircle />
                </RetryIconContainer>
              </RetryButton>
            )}
          </AnimatePresence>

          {walletInfo.iconShape === 'circle' ? (
            <CircleSpinner
              logo={
                status === States.UNAVAILABLE ? (
                  <div
                    style={{
                      transform: isPasskey ? 'scale(1)' : 'scale(1.14)',
                      position: 'relative',
                      width: '100%',
                    }}
                  >
                    {walletInfo.icon}
                  </div>
                ) : (
                  <>{walletInfo.icon}</>
                )
              }
              smallLogo={walletInfo.iconShouldShrink}
              connecting={status === States.CONNECTING}
              unavailable={status === States.UNAVAILABLE}
            />
          ) : (
            <SquircleSpinner
              logo={
                status === States.UNAVAILABLE ? (
                  <div
                    style={{
                      position: 'relative',
                      width: '60px',
                      height: '60px',
                    }}
                  >
                    <div
                      style={{
                        transform: 'scale(0.75)',
                      }}
                    >
                      {walletInfo.icon}
                    </div>
                  </div>
                ) : (
                  <>{walletInfo.icon}</>
                )
              }
              connecting={status === States.CONNECTING}
            />
          )}
        </ConnectingAnimation>

        <StatusContainer variants={contentVariants} initial='animate' animate={controller}>
          <div>{connectingH}</div>
          <span>{connectingP}</span>
        </StatusContainer>
      </ConnectingContainer>
    </PageContent>
  );
};

export default Page;
